/**
 * ProjectPicker - STEP 1 of the SVM checklist flow.
 *
 * GET /api/svm/projects. Searchable (200ms debounce) card list of active (Won,
 * not closed) projects; the caller's own assignments pinned on top, the rest
 * capped at INITIAL_CAP until "Show all". Owns ProjectRow, whose narrow branch
 * stacks the label over a grey "number · customer · location" line.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getJson, SVM_PROJECTS_URL } from './svmApi';
import { BTN_GHOST, CARD, GREEN, INPUT, LABEL, useIsNarrow } from './svmModel';
import { ErrorBanner, LoadingNote } from './SvmShared';

const INITIAL_CAP = 60;

function ProjectRow({ p, narrow, onPick }) {
  // Narrow: two stacked lines (bold label / grey "number · customer · location",
  // null fields omitted). Desktop: the original four-column row.
  const subLine = narrow ? [p.globalProjectNumber, p.customer, p.location].filter(Boolean).join(' · ') : '';
  return (
    <button
      type="button"
      onClick={() => onPick(p)}
      style={{
        width: '100%',
        textAlign: 'left',
        background: '#fff',
        border: '1px solid #ECECEC',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 8,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontFamily: "'Montserrat', sans-serif",
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        ...(narrow ? { gap: 10, minHeight: 52 } : null),
      }}
    >
      {narrow ? (
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#0D0D0D', lineHeight: 1.35, wordBreak: 'break-word' }}>
            {p.projectLabel || '-'}
          </span>
          {subLine && (
            <span
              style={{
                display: 'block',
                fontSize: 11.5,
                color: '#888',
                marginTop: 3,
                lineHeight: 1.35,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {subLine}
            </span>
          )}
        </span>
      ) : (
        <>
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: '#0D0D0D',
              flex: '1.4 1 0',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.projectLabel || '-'}
          </span>
          <span style={{ fontSize: 11.5, color: '#555', fontVariantNumeric: 'tabular-nums', flex: '0 0 90px' }}>
            {p.globalProjectNumber || '-'}
          </span>
          <span
            style={{
              fontSize: 11.5,
              color: '#555',
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.customer || '-'}
          </span>
          <span
            style={{
              fontSize: 11.5,
              color: '#888',
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {p.location || '-'}
          </span>
        </>
      )}
      <span style={{ fontSize: 12, color: '#BBB' }}>›</span>
    </button>
  );
}

export default function ProjectPicker({ onPick }) {
  const narrow = useIsNarrow(); // one listener here, passed to every row
  const [state, setState] = useState({ loading: true, error: null, projects: [] });
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true, error: null, projects: [] });
    getJson(SVM_PROJECTS_URL)
      .then((d) => setState({ loading: false, error: null, projects: d.projects || [] }))
      .catch((err) => setState({ loading: false, error: err.message || 'Failed to load projects', projects: [] }));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const filtered = useMemo(() => {
    if (!debounced) return state.projects;
    return state.projects.filter((p) =>
      [p.projectLabel, p.globalProjectNumber, p.customer, p.location].some((f) =>
        String(f || '')
          .toLowerCase()
          .includes(debounced)
      )
    );
  }, [state.projects, debounced]);

  const mine = useMemo(() => filtered.filter((p) => p.mine), [filtered]);
  const rest = useMemo(() => filtered.filter((p) => !p.mine), [filtered]);
  const capped = showAll ? rest : rest.slice(0, INITIAL_CAP);

  if (state.loading) return <LoadingNote text="Loading active projects…" />;
  if (state.error) return <ErrorBanner message={`Couldn't load projects - ${state.error}`} onRetry={load} />;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: GREEN, letterSpacing: 2, textTransform: 'uppercase' }}>SVM · Checklists</div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          Pick a project to open its SVM checklists. Every tick saves instantly and is shared with the whole team.
        </p>
      </div>

      <div style={{ ...CARD, padding: '14px 16px' }}>
        <input
          style={{ ...INPUT, fontSize: 13 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by project, GLOBAL #, customer, or location…"
        />
        <div style={{ fontSize: 11, color: '#999', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
          {filtered.length} of {state.projects.length} active projects
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...CARD, textAlign: 'center', color: '#999', fontSize: 12.5, padding: 28 }}>
          {state.projects.length === 0 ? 'No active projects found.' : 'No projects match your search.'}
        </div>
      ) : (
        <>
          {mine.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ ...LABEL, color: GREEN, marginBottom: 8 }}>Your assignments</div>
              {mine.map((p) => (
                <ProjectRow key={`${p.projectLabel}|${p.globalProjectNumber}`} p={p} narrow={narrow} onPick={onPick} />
              ))}
            </div>
          )}
          {rest.length > 0 && (
            <div>
              {mine.length > 0 && <div style={{ ...LABEL, marginBottom: 8 }}>All active projects</div>}
              {capped.map((p) => (
                <ProjectRow key={`${p.projectLabel}|${p.globalProjectNumber}`} p={p} narrow={narrow} onPick={onPick} />
              ))}
              {!showAll && rest.length > INITIAL_CAP && (
                <div style={{ textAlign: 'center', marginTop: 6 }}>
                  <button type="button" style={BTN_GHOST} onClick={() => setShowAll(true)}>
                    Show all {rest.length}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
